import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SafeImage } from '@/components/ui/safe-image';
import { MapPin, Clock } from 'lucide-react';
import type { StoreProfileDetail } from '../../types';
import { ComplianceBadge } from '../ComplianceBadge';
import { ProvenanceBadge } from '../ProvenanceBadge';
import { StoreLocationMap, StoreLocationFallback } from '../StoreLocationMap';
import { OverviewSectionEditors } from '../OverviewSectionEditors';
import { StoreInstoreLocationsCard } from '../StoreInstoreLocationsCard';

interface OverviewTabProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
}

export function OverviewTab({ profile, storeId, storeIdParam }: OverviewTabProps) {
  const currentCampaigns = profile.campaigns.filter((c) => c.phase === 'current');
  const compliantCount = currentCampaigns.filter((c) => c.complianceStatus === 'compliant').length;
  const canEdit = storeId != null && storeIdParam != null;
  const galleryPreviewImages = profile.images
    .filter((img) => !img.hidden && Boolean(img.url?.trim()))
    .slice(0, 8);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2 space-y-6">
        <OverviewSectionEditors
          profile={profile}
          storeId={storeId ?? 0}
          storeIdParam={storeIdParam ?? ''}
          editable={canEdit}
        />

        {storeId != null && storeIdParam != null && (
          <StoreInstoreLocationsCard
            profile={profile}
            storeId={storeId}
            storeIdParam={storeIdParam}
            editable={canEdit}
          />
        )}

        {profile.liveContextStats && (
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                Live REACTOR Data
                <ProvenanceBadge provenance="live" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4 text-sm">
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-2xl font-bold">{profile.totalMediaLocations}</p>
                  <p className="text-gray-500">Media locations</p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-2xl font-bold">{profile.totalMediaCapacity}</p>
                  <p className="text-gray-500">Total capacity</p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-2xl font-bold">{profile.liveContextStats.packCount}</p>
                  <p className="text-gray-500">Packs featured</p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-2xl font-bold">{profile.currentCampaignCount ?? 0}</p>
                  <p className="text-gray-500">Active campaigns</p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-2xl font-bold">{profile.campaignsToDateCount ?? '—'}</p>
                  <p className="text-gray-500">Campaigns to date</p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-2xl font-bold">{profile.liveContextStats.instoreLocationCount}</p>
                  <p className="text-gray-500">Posting locations</p>
                </div>
              </div>
              {profile.livePacks && profile.livePacks.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Packs this store features in</p>
                  {profile.livePacks.slice(0, 6).map((pack) => (
                    <div key={pack.id} className="text-sm border rounded-lg p-3 bg-white">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{pack.name}</span>
                        {!pack.isActive && <Badge variant="secondary">Inactive</Badge>}
                      </div>
                      {pack.templates.length > 0 && (
                        <p className="text-gray-500 mt-1">
                          {pack.templates.map((t) => `${t.mediaFormatName} ×${t.quantity}`).join(' · ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Current Campaign Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-700">{currentCampaigns.length}</p>
                <p className="text-xs text-blue-600">Active campaigns</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">{compliantCount}</p>
                <p className="text-xs text-green-600">Fully compliant</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-700">{currentCampaigns.length - compliantCount}</p>
                <p className="text-xs text-amber-600">Need attention</p>
              </div>
            </div>
            <div className="space-y-2">
              {currentCampaigns.slice(0, 5).map((c) => (
                <div key={c.id} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                  <div>
                    <span className="font-medium">{c.campaignName}</span>
                    <span className="text-gray-500 ml-2">— {c.brand}</span>
                  </div>
                  <ComplianceBadge status={c.complianceStatus} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Image Gallery Preview</CardTitle>
          </CardHeader>
          <CardContent>
            {galleryPreviewImages.length === 0 ? (
              <p className="text-sm text-gray-500">No visible gallery photos yet.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {galleryPreviewImages.map((img) => (
                  <div key={img.id} className="relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-100">
                    <SafeImage
                      src={img.url}
                      alt={img.caption}
                      className="w-full h-full object-cover"
                      containerClassName="absolute inset-0"
                      placeholderType="photo"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate">{img.caption}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Media Inventory Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Total locations</span><span className="font-medium">{profile.mediaLocations.length}</span></div>
            <div className="flex justify-between"><span>In campaign</span><span>{profile.mediaLocations.filter((m) => m.availability === 'in_campaign').length}</span></div>
            <div className="flex justify-between"><span>Available</span><span>{profile.mediaLocations.filter((m) => m.availability === 'available').length}</span></div>
            <div className="flex justify-between"><span>Unavailable / damaged</span><span>{profile.mediaLocations.filter((m) => m.availability === 'unavailable' || m.availability === 'damaged').length}</span></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" />Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {profile.recentActivity.map((item) => (
                <div key={item.id} className="border-l-2 border-blue-200 pl-3">
                  <p className="text-xs text-gray-500">{item.date}</p>
                  <p className="text-sm font-medium">{item.title}</p>
                  <p className="text-xs text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" />Location</CardTitle>
          </CardHeader>
          <CardContent>
            {profile.coordinates ? (
              <StoreLocationMap
                lat={profile.coordinates.lat}
                lng={profile.coordinates.lng}
                address={profile.fullAddress}
              />
            ) : (
              <StoreLocationFallback address={profile.fullAddress} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
